/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, {
  createContext,
  startTransition,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import baselineWhereBundled from 'virtual:ere-i18n-baseline';
import { useSSRTranslations } from './SSRTranslationsContext';
import { deepMerge } from '../lib/deepMerge';

declare global {
  interface Window {
    // Env-merged i18n inlined into every HTML response — the page's own
    // language only, and of it only the namespaces the page's route family
    // reads (src/lib/i18nSeedScope.js): the bake writes it into prerendered pages
    // (scripts/prerender.mjs), server/spaRenderer.ts into the SPA shell and
    // the dev page — so the browser starts with the same translations the
    // server used, without carrying any baseline in its bundle.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __INITIAL_I18N__?: Partial<Record<'nl' | 'en', any>>;
  }
}

export type Language = 'nl' | 'en';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Tree = any;
type Trees = Partial<Record<Language, Tree>>;

/**
 * The bundled baseline, where it IS bundled: the SSR bundle (renderApp may be
 * called without translations) and the unit tests, which render components
 * without a seed. In the BROWSER build the virtual module is `null`
 * (scripts/vite-plugin-i18n-baseline.mjs), so neither JSON file is linked
 * into the entry chunk — ~480 KB of source, ≈90 KB compressed, until
 * 2026-10-01. The browser gets the page's language inline from its HTML
 * (window.__INITIAL_I18N__), the other one from GET /api/content when the
 * visitor switches, and the baseline only as a lazy chunk, for the day
 * neither of those is there. A static import of the JSON files would undo
 * this: one static importer anywhere pins a module into the entry chunk.
 */
let bundledBaseline: Trees | null = baselineWhereBundled;

/** Test hook: run the provider the way the browser build does (no bundled baseline). */
export const __setBundledBaselineForTests = (value: Trees | null): void => {
  bundledBaseline = value;
};

const loadBaselineChunk = (lang: Language): Promise<Tree> =>
  (lang === 'nl' ? import('../i18n/nl.json') : import('../i18n/en.json')).then((m) => m.default);

/** How long a language switch waits for that language's copy before it flips anyway. */
const SWITCH_WAIT_MS = 2000;

const isTree = (value: unknown): value is Tree =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const fetchLiveTree = (lang: Language): Promise<Tree | null> =>
  fetch(`/api/content?lang=${lang}`)
    .then((r) => (r.ok ? r.json() : null))
    // Defense-in-depth: a malformed API result (string, array, null) must
    // never blow up deepMerge or quietly replace the copy with garbage.
    .then((live) => (isTree(live) ? live : null))
    .catch(() => null);

/** How long the rest of a seeded language waits for an idle moment, at most. */
const REST_IDLE_TIMEOUT_MS = 2000;

/** Run `task` when the browser is idle; returns the cancel. */
const whenIdle = (task: () => void): (() => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(task, { timeout: REST_IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(task, REST_IDLE_TIMEOUT_MS / 2);
  return () => window.clearTimeout(id);
};

/** `tree` plus the namespaces of `rest` it does not have — `tree` itself when there are none. */
const withMissingNamespaces = (tree: Tree, rest: Tree): Tree => {
  const missing = Object.keys(rest).filter((ns) => !(ns in tree));
  if (missing.length === 0) return tree;
  return { ...tree, ...Object.fromEntries(missing.map((ns) => [ns, rest[ns]])) };
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: { returnObjects?: boolean }) => any;
  /**
   * Raw `seo` map for the active language (merged static baseline + admin
   * overrides). Exposed because `t()` cannot return objects/booleans without
   * losing the empty-vs-missing distinction needed by per-article overrides.
   */
  seoOverrides: Record<string, string | boolean>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function langFromPath(pathname: string): Language {
  const parts = pathname.split('/');
  return parts[1] === 'en' ? 'en' : 'nl';
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const ssrTranslations = useSSRTranslations();
  // Initial language is derived from the URL only — deterministic on both
  // server (StaticRouter location) and client (BrowserRouter location), so
  // hydration matches without a `mounted` gate. localStorage preferences
  // are reconciled in a useEffect after hydration.
  const [language, setLanguageState] = useState<Language>(() => langFromPath(location.pathname));

  // Initial translations come from the highest-fidelity source available:
  //   1. SSR context (server-side render with env-merged i18n)
  //   2. window.__INITIAL_I18N__ (every HTML response seeds the page's language)
  //   3. The bundled baseline — SSR bundle and tests only, see above
  // This ensures the rendered HTML and first client paint use the env's
  // actual CMS copy, eliminating the "Verdien… → Ontvang…" flash.
  //
  // A page seeds its own language only, scoped to its route family (the rest
  // of the tree follows after hydration, below). The server and the
  // hydrating client build this from the same seed object, so their start
  // states cannot differ for the language being rendered.
  const seed =
    ssrTranslations ?? (typeof window !== 'undefined' ? window.__INITIAL_I18N__ : undefined);
  const [translations, setTranslations] = useState<Trees>(() => ({
    ...(bundledBaseline ?? {}),
    ...seed,
  }));

  // Languages whose state already holds the env's live copy, so the next
  // mount-or-switch fetch for them can be skipped once. The other language
  // (and any subsequent admin-driven refreshes) still go through the API.
  const freshLangs = useRef<Set<Language>>(new Set(seed ? (Object.keys(seed) as Language[]) : []));

  const mergeTree = useCallback((lang: Language, tree: Tree) => {
    setTranslations((prev) => ({
      ...prev,
      [lang]: prev[lang] ? deepMerge(prev[lang], tree) : tree,
    }));
  }, []);

  // The rest of a seeded language. A page seeds only the namespaces its route
  // family reads (src/lib/i18nSeedScope.js, 2026-10-15); the others are
  // fetched once after hydration, when the browser is idle, so a client-side
  // move to another family finds its copy. They are held here, outside state:
  // the page on screen reads none of them, so their arrival re-renders
  // nothing ("Nothing re-renders the page right after hydration") — unless a
  // render already asked for a namespace that was not there yet (`restMissed`,
  // a move before the fetch landed), and then they join the state once, in a
  // transition. Such a render also starts the fetch at once instead of on idle.
  const restTrees = useRef<Trees>({});
  const restRequested = useRef<Set<Language>>(new Set());
  const restMissed = useRef(false);
  const loadRest = useCallback((lang: Language) => {
    if (restRequested.current.has(lang)) return;
    restRequested.current.add(lang);
    fetchLiveTree(lang)
      // The API is down: the baseline chunk is better than key paths.
      .then((live) => live ?? loadBaselineChunk(lang).catch(() => null))
      .then((rest: Tree | null) => {
        if (!isTree(rest)) {
          restRequested.current.delete(lang); // a later miss may try again
          return;
        }
        restTrees.current = { ...restTrees.current, [lang]: rest };
        if (!restMissed.current) return;
        restMissed.current = false;
        startTransition(() => {
          setTranslations((prev) => {
            const tree = prev[lang];
            if (!isTree(tree)) return prev;
            const next = withMissingNamespaces(tree, rest);
            return next === tree ? prev : { ...prev, [lang]: next };
          });
        });
      });
  }, []);
  // Once after hydration: the languages the page was seeded with.
  const seededLangs = useRef(seed ? (Object.keys(seed) as Language[]) : []);
  useEffect(() => {
    const langs = seededLangs.current;
    if (langs.length === 0) return;
    return whenIdle(() => langs.forEach(loadRest));
  }, [loadRest]);
  // A render asked for a namespace the state does not hold: fetch now.
  useEffect(() => {
    if (restMissed.current) loadRest(language);
  });

  // Keep state in sync with URL changes (e.g. when navigate() switches lang).
  // language is intentionally excluded from deps — the effect only cares about
  // pathname changes, and including language would cause a re-run loop.
  useEffect(() => {
    const next = langFromPath(location.pathname);
    if (next !== language) {
      setLanguageState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Fetch live (admin-edited) translations and deep-merge over whatever the
  // state holds for the language. Skipped once for a language the server
  // seeded (or a switch prefetched) — that copy is already the latest. Runs
  // on every later language switch, so a CMS edit reaches a visitor who
  // switches back.
  useEffect(() => {
    if (freshLangs.current.has(language)) {
      freshLangs.current.delete(language);
      return;
    }
    let cancelled = false;
    const missing = !translations[language];
    // Nothing at all for this language yet (a page served without its seed):
    // the baseline chunk is the quickest way to real sentences, the live
    // copy replaces it when it lands.
    if (missing) {
      loadBaselineChunk(language)
        .then((tree) => {
          if (cancelled) return;
          setTranslations((prev) => (prev[language] ? prev : { ...prev, [language]: tree }));
        })
        .catch(() => {});
    }
    fetchLiveTree(language).then((live) => {
      if (cancelled || !live) return;
      mergeTree(language, live);
    });
    return () => {
      cancelled = true;
    };
    // translations is read once, at the moment the language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, mergeTree]);

  // After hydration, persist the URL-derived language so the user's last
  // explicit choice is remembered for any future bare-`/` visit. Mirror to a
  // cookie so server-side `/` and unprefixed-path redirects can honour the
  // preference before any client JS runs.
  useEffect(() => {
    try {
      localStorage.setItem('language', language);
    } catch {
      /* ignore (private browsing, etc.) */
    }
    try {
      const oneYear = 60 * 60 * 24 * 365;
      document.cookie = `lang=${language}; path=/; max-age=${oneYear}; samesite=lax`;
    } catch {
      /* ignore */
    }
  }, [language]);

  const switchSeq = useRef(0);
  const setLanguage = useCallback(
    (lang: Language) => {
      if (lang === language) return;
      // Update the URL — the location effect above will then update state.
      // Keep search AND hash: the partner portal keeps its open tab in the
      // hash (#aanbod), and in-page anchors elsewhere should survive NL↔EN too.
      const go = () => {
        const parts = location.pathname.split('/');
        if (parts[1] === 'nl' || parts[1] === 'en') {
          parts[1] = lang;
          navigate(parts.join('/') + location.search + location.hash, { replace: false });
        } else {
          navigate(`/${lang}${location.pathname}${location.search}${location.hash}`, {
            replace: false,
          });
        }
      };
      if (translations[lang]) {
        go();
        return;
      }
      // The other language is not in memory (the browser bundle carries no
      // baseline): get its copy BEFORE the URL flips, so the switch never
      // shows key paths while the request is in flight. The live copy is
      // what should land; the baseline chunk is the stand-in when the API
      // is slow or down; after SWITCH_WAIT_MS the page flips regardless and
      // the fetch effect above finishes the job.
      const seq = ++switchSeq.current;
      let done = false;
      const finish = (tree: Tree | null) => {
        if (done || seq !== switchSeq.current) return;
        done = true;
        if (tree) {
          mergeTree(lang, tree);
          freshLangs.current.add(lang);
        }
        go();
      };
      const timer = setTimeout(() => {
        loadBaselineChunk(lang).then(finish, () => finish(null));
      }, SWITCH_WAIT_MS);
      fetchLiveTree(lang).then((live) => {
        if (live) {
          clearTimeout(timer);
          finish(live);
          return;
        }
        clearTimeout(timer);
        loadBaselineChunk(lang).then(finish, () => finish(null));
      });
    },
    [language, location.pathname, location.search, location.hash, navigate, translations, mergeTree]
  );

  // MUST stay referentially stable across renders that don't change the
  // translations or the language. `t` is a dependency of effects all over the
  // app (useSEO, useFAQSchema, the partner-portal fetches); when it was a plain
  // inline function it got a new identity on every provider render, so those
  // effects re-ran constantly — the partner profile editor refetched on every
  // keystroke and overwrote the user's unsaved text with the server copy.
  const t = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, options?: { returnObjects?: boolean }): any => {
      const keys = key.split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const walk = (root: any): any => {
        let node = root;
        for (const k of keys) {
          if (node != null && typeof node === 'object' && k in node) {
            node = node[k];
          } else {
            return undefined;
          }
        }
        return node;
      };

      const tree = translations[language];
      let result = walk(tree);
      if (result === undefined && isTree(tree) && !(keys[0] in tree)) {
        // A namespace this page's seed left out (another route family's):
        // the rest of the tree once it has landed, else the key path — on
        // the server too, never the bundled baseline, so both sides of
        // hydration read the same thing.
        const rest = restTrees.current[language];
        if (isTree(rest) && keys[0] in rest) result = walk(rest);
        // Only a key that looks like one (`ns.leaf`) asks for the rest early.
        else if (keys.length > 1 && /^[a-z][a-z0-9_]*$/.test(keys[0])) restMissed.current = true;
      } else if (result === undefined && bundledBaseline) {
        // The bundled baseline is the second stop where it exists (SSR,
        // tests) — the seed is the baseline plus the env's overrides, so in
        // the browser a key missing from a seeded namespace is missing
        // everywhere, and the chain ends in the same key path on both sides.
        result = walk(bundledBaseline[language]);
      }

      if (result === undefined) return key;
      if (options?.returnObjects) return result;
      return typeof result === 'object' ? key : String(result);
    },
    [translations, language]
  );

  const seoOverrides: Record<string, string | boolean> = useMemo(
    () => (translations[language]?.seo as Record<string, string | boolean> | undefined) ?? {},
    [translations, language]
  );

  // Memoised too: a fresh object here would re-render every consumer on each
  // provider render, defeating the stable `t` above.
  const value = useMemo(
    () => ({ language, setLanguage, t, seoOverrides }),
    [language, setLanguage, t, seoOverrides]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
